/**
 * The push enrollment banner — V7-01 §2.
 *
 * The founder's ruling: "relying on every user to find and press a settings
 * button is not a solution." He is right, and the honest constraint recorded
 * in §2 is that no browser will let us go all the way — a gesture and a
 * per-browser grant are required by design. So the achievable maximum ships:
 * **the prompt comes to the person** instead of waiting to be found, and after
 * one grant they never touch it again on that browser (`ensureSubscribed`).
 *
 * The cadence lives in `pushBannerState.ts` as pure functions over an injected
 * clock; this component is the wiring and the words. It renders nothing at all
 * for guests, unsupported browsers, blocked sites and people who have said no
 * three times — §2.1 says "shown instead: nothing", not a disabled control
 * explaining a thing they cannot do.
 */

import { useCallback, useEffect, useState } from 'react';
import { BellRing, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/features/auth';
import {
  PushSetupError,
  pushSupported,
  requestPushPermission,
  subscribeThisDevice,
  usePushDevices,
} from './pushApi';
import {
  dismissBanner,
  enrolled,
  markShown,
  parseBannerState,
  shouldShowBanner,
  stopAsking,
  wantsPush,
  type BannerState,
} from './pushBannerState';

const STORAGE_KEY = 'vt.push.banner';
const SESSION_KEY = 'vt.push.session';

function loadState(): BannerState {
  try {
    return parseBannerState(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return parseBannerState(null);
  }
}

function saveState(state: BannerState): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Private windows refuse writes. Losing the cadence is a worse banner, not
    // a broken app — and a private window cannot subscribe anyway.
  }
}

/** One id per tab-run, which is what "once per session" means here. */
function sessionId(): string {
  try {
    const existing = window.sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const fresh = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    window.sessionStorage.setItem(SESSION_KEY, fresh);
    return fresh;
  } catch {
    return 'no-session-storage';
  }
}

export function PushEnrollmentBanner() {
  const { user, status } = useAuth();
  const { data: devices } = usePushDevices();
  const [state, setState] = useState<BannerState>(() => loadState());
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  const isGuest = user?.systemRole === 'guest';

  /**
   * Whether to ask at all.
   *
   * §2.1 targets "anyone with push events enabled in prefs" — see `wantsPush`
   * for why that cannot be read literally. Someone who turned push off after
   * having had it has answered the question, and asking again would be
   * overriding them; someone who has never had a device has not.
   */
  const anyPushRowOn = Object.values(user?.preferences?.notificationMatrix ?? {}).some(
    (row) => row?.push === true,
  );
  const pushPreferenceOn = wantsPush({
    anyPushRowOn,
    // The banner only ever renders where `devices` is loaded; an empty list
    // with rows ticked means they had a device and removed it.
    hasEverRegisteredADevice: anyPushRowOn,
  });

  const evaluate = useCallback(() => {
    if (status !== 'authenticated' || !user || devices === undefined) return;

    const show = shouldShowBanner({
      state,
      now: Date.now(),
      sessionId: sessionId(),
      supported: pushSupported(),
      isGuest: Boolean(isGuest),
      permission: pushSupported() ? Notification.permission : 'denied',
      hasSubscription: devices.length > 0,
      pushPreferenceOn,
    });

    if (!show) return;
    setVisible(true);
    setState((current) => {
      const next = markShown(current, sessionId());
      saveState(next);
      return next;
    });
  }, [status, user, devices, state, isGuest, pushPreferenceOn]);

  /**
   * Re-evaluated when the identity or the device list changes, and never on
   * `state` — `markShown` writes it, so depending on it would loop. `evaluate`
   * is deliberately not a dependency for the same reason: it closes over
   * `state`, which changes the moment the banner is shown.
   */
  const identity = user?._id;
  const deviceCount = devices?.length;
  useEffect(() => {
    evaluate();
  }, [status, identity, deviceCount]);

  if (!visible) return null;

  /**
   * The gesture.
   *
   * `requestPushPermission()` is the first statement, with nothing awaited
   * before it — that ordering is the whole of the V7-01 root-cause fix, and
   * `pushGesture.test.ts` enforces it structurally.
   */
  async function turnOn(): Promise<void> {
    const permission = await requestPushPermission();
    setBusy(true);
    try {
      if (permission !== 'granted') {
        toast.error(
          permission === 'denied'
            ? 'Notifications are blocked for this site. Settings → Notifications explains how to unblock them.'
            : 'The browser prompt was closed without a choice — press Turn on again when you are ready.',
        );
        return;
      }
      const device = await subscribeThisDevice();
      setState((current) => {
        const next = enrolled(current);
        saveState(next);
        return next;
      });
      setVisible(false);
      toast.success(`Notifications are on for ${device.label}.`);
    } catch (err) {
      toast.error(
        err instanceof PushSetupError ? err.message : 'Could not turn on notifications here.',
      );
    } finally {
      setBusy(false);
    }
  }

  function notNow(): void {
    setState((current) => {
      const next = dismissBanner(current, Date.now());
      saveState(next);
      return next;
    });
    setVisible(false);
  }

  function never(): void {
    setState((current) => {
      const next = stopAsking(current);
      saveState(next);
      return next;
    });
    setVisible(false);
  }

  return (
    <div
      role="status"
      data-testid="push-enrollment-banner"
      className="flex flex-wrap items-center gap-2 border-b border-[var(--border)] bg-[var(--accent-soft)] px-4 py-2 text-[12px]"
    >
      <BellRing className="size-4 shrink-0 text-[var(--accent)]" aria-hidden />
      <span className="min-w-0 flex-1">
        Get notified when tasks and messages reach you — even when VirtueTasks is closed.
      </span>
      <Button variant="primary" size="sm" data-testid="push-banner-turn-on" disabled={busy} onClick={() => void turnOn()}>
        {busy ? <Loader2 className="size-3.5 animate-spin" /> : null}
        Turn on
      </Button>
      <Button variant="ghost" size="sm" data-testid="push-banner-not-now" onClick={notNow}>
        Not now
      </Button>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Don’t ask again"
        data-testid="push-banner-never"
        onClick={never}
      >
        <X className="size-3.5" />
      </Button>
    </div>
  );
}
